
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Heart, Calendar, Gift, Shield, Users, Lightbulb } from "lucide-react";

const New2Benefits = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Pourquoi choisir Legatia ?",
      subtitle: "Une approche unique qui combine émotion, sécurité et croissance",
      benefits: [
        {
          icon: Heart,
          title: "Connection émotionnelle profonde",
          description: "Créez un lien intemporel avec votre enfant grâce à des messages personnalisés qui l'accompagneront aux moments clés de sa vie."
        },
        {
          icon: Gift,
          title: "Contribution qui grandit",
          description: "Votre investissement croît de manière éthique et durable, offrant un vrai soutien financier au moment choisi."
        },
        {
          icon: Calendar,
          title: "Timing parfait",
          description: "Choisissez le moment exact où votre cadeau sera révélé : 18 ans, diplôme, mariage, ou tout autre événement important."
        },
        {
          icon: Shield,
          title: "Sécurité maximale",
          description: "Vos données et contributions sont protégées selon les standards bancaires suisses les plus stricts."
        },
        {
          icon: Users,
          title: "Expérience familiale",
          description: "Invitez la famille élargie à participer et créer ensemble une capsule temporelle collective."
        },
        {
          icon: Lightbulb,
          title: "Innovation responsable",
          description: "Une approche moderne et éthique de la transmission intergénérationnelle, pensée pour durer."
        }
      ]
    },
    en: {
      title: "Why choose Legatia?",
      subtitle: "A unique approach that combines emotion, security, and growth",
      benefits: [
        {
          icon: Heart,
          title: "Deep emotional connection",
          description: "Create a timeless bond with your child through personalized messages that will accompany them at key moments in their life."
        },
        {
          icon: Gift,
          title: "Growing contribution",
          description: "Your investment grows ethically and sustainably, providing real financial support at the chosen moment."
        },
        {
          icon: Calendar,
          title: "Perfect timing",
          description: "Choose the exact moment when your gift will be revealed: 18 years old, graduation, marriage, or any other important event."
        },
        {
          icon: Shield,
          title: "Maximum security",
          description: "Your data and contributions are protected according to the strictest Swiss banking standards."
        },
        {
          icon: Users,
          title: "Family experience",
          description: "Invite the extended family to participate and create a collective time capsule together."
        },
        {
          icon: Lightbulb,
          title: "Responsible innovation",
          description: "A modern and ethical approach to intergenerational transmission, designed to last."
        }
      ]
    },
    de: {
      title: "Warum Legatia wählen?",
      subtitle: "Ein einzigartiger Ansatz, der Emotion, Sicherheit und Wachstum kombiniert",
      benefits: [
        {
          icon: Heart,
          title: "Tiefe emotionale Verbindung",
          description: "Schaffen Sie eine zeitlose Bindung zu Ihrem Kind durch personalisierte Nachrichten, die es in den Schlüsselmomenten seines Lebens begleiten werden."
        },
        {
          icon: Gift,
          title: "Wachsender Beitrag",
          description: "Ihre Investition wächst ethisch und nachhaltig und bietet echte finanzielle Unterstützung zum gewählten Zeitpunkt."
        },
        {
          icon: Calendar,
          title: "Perfektes Timing",
          description: "Wählen Sie den genauen Moment, in dem Ihr Geschenk enthüllt wird: 18 Jahre alt, Abschluss, Hochzeit oder ein anderes wichtiges Ereignis."
        },
        {
          icon: Shield,
          title: "Maximale Sicherheit",
          description: "Ihre Daten und Beiträge sind nach den strengsten Schweizer Bankenstandards geschützt."
        },
        {
          icon: Users,
          title: "Familienerfahrung",
          description: "Laden Sie die erweiterte Familie ein, teilzunehmen und gemeinsam eine kollektive Zeitkapsel zu erstellen."
        },
        {
          icon: Lightbulb,
          title: "Verantwortungsvolle Innovation",
          description: "Ein moderner und ethischer Ansatz für die intergenerationelle Übertragung, der für die Dauer konzipiert ist."
        }
      ]
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content[language].title}
          </h2>
          <p className="text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {content[language].benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="bg-legatia-orange/10 p-3 rounded-full w-fit mb-6">
                <benefit.icon className="h-6 w-6 text-legatia-orange" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default New2Benefits;
